import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc, doc, query, orderBy, limit, serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, auth, storage } from "../firebase/firebase-config";

const SITE_URL = "https://pp-studio-website.web.app";

const EMPTY_ISSUE = {
  lists: ["studio"],
  kicker: "",
  title: "",
  quote: "",
  body: "",
  attachedContent: null,
  question: "",
  signoff: "With warmth,\nPooja",
  headerImage: "",
  video: null,
  links: [],
};

function toHtmlPreview(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => p.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\n/g, "<br/>"))
    .map(p => `<p>${p}</p>`)
    .join("");
}

const YOUTUBE_ID_RE = /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
function extractYouTubeId(url) {
  const match = String(url || "").match(YOUTUBE_ID_RE);
  return match ? match[1] : null;
}

export default function AdminNewsletter() {
  const [form, setForm] = useState(EMPTY_ISSUE);
  const [issueId, setIssueId] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);

  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);
  const [headerPickerOpen, setHeaderPickerOpen] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [headerUrlInput, setHeaderUrlInput] = useState("");

  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [videoTitleInput, setVideoTitleInput] = useState("");

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const loadIssues = useCallback(async () => {
    setLoadingIssues(true);
    try {
      const snap = await getDocs(query(collection(db, "newsletter_issues"), orderBy("updatedAt", "desc"), limit(30)));
      setIssues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (_) { /* keep whatever was already loaded */ }
    finally { setLoadingIssues(false); }
  }, []);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const openLibrary = async () => {
    setAttachPickerOpen(true);
    if (library.length) return;
    setLibraryLoading(true);
    try {
      const [blogsSnap, tipsSnap, podSnap] = await Promise.all([
        getDocs(query(collection(db, "blogs"), orderBy("createdAt", "desc"), limit(20))),
        getDocs(query(collection(db, "contract_tips"), orderBy("createdAt", "desc"), limit(20))),
        getDocs(query(collection(db, "podcast_episodes"), orderBy("episodeNum", "desc"), limit(20))),
      ]);
      const blogs = blogsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.draft !== true)
        .map(p => ({
          type: "blog",
          title: p.title,
          excerpt: p.excerpt || "",
          image: p.thumbnail || "",
          url: `${SITE_URL}/blog/${p.slug || p.id}`,
        }));
      const tips = tipsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.draft !== true)
        .map(p => ({
          type: "tip",
          title: p.title,
          excerpt: p.excerpt || "",
          image: p.thumbnail || "",
          url: `${SITE_URL}/contract-tips/${p.id}`,
        }));
      const pods = podSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.published)
        .map(p => ({
          type: "podcast",
          title: p.title,
          excerpt: [p.guest, p.guestTitle].filter(Boolean).join(" — ") || p.description || "",
          image: p.coverArt || "",
          url: `${SITE_URL}/podcast`,
        }));
      setLibrary([...blogs, ...tips, ...pods]);
    } catch (_) {
      setError("Could not load your posts and episodes to attach.");
    } finally {
      setLibraryLoading(false);
    }
  };

  const pickAttachment = (item) => {
    set("attachedContent", item);
    setAttachPickerOpen(false);
    if (!form.title) set("title", item.title);
  };

  const openMediaLibrary = async () => {
    setHeaderPickerOpen(true);
    if (mediaLibrary.length) return;
    setMediaLibraryLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "media_library"), orderBy("uploadedAt", "desc")));
      setMediaLibrary(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(item => item.url));
    } catch (_) {
      setError("Could not load the Media Library.");
    } finally {
      setMediaLibraryLoading(false);
    }
  };

  const chooseHeaderFromLibrary = (item) => {
    set("headerImage", item.url);
    setHeaderPickerOpen(false);
  };

  const uploadHeaderImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setUploadingHeader(true);
    setUploadProgress(0);
    setError("");
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const storageRef = ref(storage, `newsletter-images/${Date.now()}.${extension}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed", snap => {
      setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
    }, () => {
      setUploadingHeader(false);
      setError("Upload failed. Please try again.");
    }, async () => {
      try {
        const url = await getDownloadURL(task.snapshot.ref);
        set("headerImage", url);
        setStatus("Header image uploaded.");
      } catch (_) {
        setError("The image uploaded, but we couldn't attach it. Please try again.");
      } finally {
        setUploadingHeader(false);
        setUploadProgress(0);
      }
    });
  };

  const applyHeaderUrl = () => {
    const url = headerUrlInput.trim();
    if (!/^https?:\/\//i.test(url)) {
      setError("Please paste a valid image URL beginning with http:// or https://.");
      return;
    }
    set("headerImage", url);
    setHeaderUrlInput("");
  };

  const attachVideo = () => {
    const id = extractYouTubeId(videoUrlInput);
    if (!id) {
      setError("That doesn't look like a YouTube link. Paste the full video URL.");
      return;
    }
    setError("");
    set("video", {
      url: `https://www.youtube.com/watch?v=${id}`,
      thumbnail: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
      title: videoTitleInput.trim(),
    });
    setVideoUrlInput("");
    setVideoTitleInput("");
  };

  const addLink = () => set("links", [...form.links, { label: "", url: "" }]);
  const updateLink = (i, field, value) => {
    const next = form.links.map((l, idx) => idx === i ? { ...l, [field]: value } : l);
    set("links", next);
  };
  const removeLink = (i) => set("links", form.links.filter((_, idx) => idx !== i));

  const toggleList = (list) => {
    setForm(prev => ({
      ...prev,
      lists: prev.lists.includes(list) ? prev.lists.filter(l => l !== list) : [...prev.lists, list],
    }));
  };

  const newDraft = () => {
    setIssueId(null);
    setForm(EMPTY_ISSUE);
    setStatus("");
    setError("");
  };

  const loadIssue = (issue) => {
    setIssueId(issue.id);
    setForm({
      lists: issue.lists || ["studio"],
      kicker: issue.kicker || "",
      title: issue.title || "",
      quote: issue.quote || "",
      body: issue.body || "",
      attachedContent: issue.attachedContent || null,
      question: issue.question || "",
      signoff: issue.signoff || "With warmth,\nPooja",
      headerImage: issue.headerImage || "",
      video: issue.video || null,
      links: issue.links || [],
    });
    setStatus("");
    setError("");
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    setStatus("");
    try {
      if (form.lists.length === 0) throw new Error("Choose at least one audience.");
      const payload = { ...form, subject: form.title || form.kicker || "A note from Pooja", updatedAt: serverTimestamp() };
      if (issueId) {
        await setDoc(doc(db, "newsletter_issues", issueId), payload, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "newsletter_issues"), {
          ...payload, status: "draft", createdAt: serverTimestamp(),
        });
        setIssueId(ref.id);
      }
      setStatus("Draft saved.");
      await loadIssues();
    } catch (err) {
      setError(err.message || "Could not save this draft.");
    } finally {
      setSaving(false);
    }
  };

  const deleteIssue = async (id) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    try {
      await deleteDoc(doc(db, "newsletter_issues", id));
      if (id === issueId) newDraft();
      await loadIssues();
    } catch (_) {
      setError("Could not delete this draft.");
    }
  };

  const sendNow = async () => {
    if (!issueId) {
      setError("Save this as a draft first — then you can send it.");
      return;
    }
    const audienceLabel = form.lists.map(l => l === "studio" ? "The PP Studio" : "The PP Edit").join(" and ");
    if (!window.confirm(`Send this to every active subscriber of ${audienceLabel}? This can't be undone.`)) return;
    setSending(true);
    setError("");
    setStatus("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/admin/newsletter/send", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ issueId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not send this issue.");
      setStatus(`Sent to ${data.recipientCount} subscriber${data.recipientCount === 1 ? "" : "s"}.`);
      await loadIssues();
    } catch (err) {
      setError(err.message || "Could not send this issue.");
    } finally {
      setSending(false);
    }
  };

  const isSent = useMemo(() => issues.find(i => i.id === issueId)?.status === "sent", [issues, issueId]);
  const previewTheme = form.lists.includes("pp_edit") && !form.lists.includes("studio") ? "edit" : "studio";

  return (
    <div className="adm-nl">
      <div className="adm-nl-head">
        <div>
          <span className="adm-nl-eyebrow">NEWSLETTER</span>
          <h2>Write an issue, or attach a post — then send it</h2>
          <p>Draft freely, save as you go, and send whenever it's ready. Nothing goes out until you press Send.</p>
        </div>
        <button type="button" className="adm-nl-new-btn" onClick={newDraft}>+ New draft</button>
      </div>

      {status && <p className="adm-nl-status adm-nl-status-ok">{status}</p>}
      {error && <p className="adm-nl-status adm-nl-status-err">{error}</p>}

      <div className="adm-nl-layout">
        <div className="adm-nl-controls">
          <section>
            <h3>Who's this for?</h3>
            <div className="adm-nl-audience">
              <label className={`adm-nl-audience-opt${form.lists.includes("studio") ? " checked" : ""}`}>
                <input type="checkbox" checked={form.lists.includes("studio")} onChange={() => toggleList("studio")} />
                <span><strong>The PP Studio</strong><em>Contract tips, business insight</em></span>
              </label>
              <label className={`adm-nl-audience-opt${form.lists.includes("pp_edit") ? " checked" : ""}`}>
                <input type="checkbox" checked={form.lists.includes("pp_edit")} onChange={() => toggleList("pp_edit")} />
                <span><strong>The PP Edit</strong><em>Personal stories, reflections</em></span>
              </label>
            </div>
          </section>

          <section>
            <h3>Header image</h3>
            <p>Optional — a banner image across the top of the issue.</p>
            {form.headerImage ? (
              <div className="adm-nl-attached">
                <img src={form.headerImage} alt="" />
                <div><strong>Header image set</strong></div>
                <button type="button" onClick={() => set("headerImage", "")}>Remove</button>
              </div>
            ) : (
              <>
                <div className="adm-nl-media-row">
                  <button type="button" className="adm-nl-attach-btn" onClick={openMediaLibrary}>+ Choose from Media Library</button>
                  <label className="adm-nl-upload-btn">
                    {uploadingHeader ? `Uploading ${uploadProgress}%` : "Upload an image"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      hidden
                      disabled={uploadingHeader}
                      onChange={e => { uploadHeaderImage(e.target.files?.[0]); e.target.value = ""; }}
                    />
                  </label>
                </div>
                <div className="adm-nl-url-row">
                  <input
                    type="url"
                    value={headerUrlInput}
                    onChange={e => setHeaderUrlInput(e.target.value)}
                    placeholder="or paste an image URL…"
                    onKeyDown={e => { if (e.key === "Enter") applyHeaderUrl(); }}
                  />
                  <button type="button" onClick={applyHeaderUrl} disabled={!headerUrlInput.trim()}>Use</button>
                </div>
              </>
            )}
            {headerPickerOpen && (
              <div className="adm-nl-library">
                <div className="adm-nl-library-head">
                  <span>{mediaLibraryLoading ? "Loading…" : `${mediaLibrary.length} images`}</span>
                  <button type="button" onClick={() => setHeaderPickerOpen(false)}>✕</button>
                </div>
                <div className="adm-nl-library-list adm-nl-library-grid">
                  {mediaLibrary.map(item => (
                    <button type="button" key={item.id} className="adm-nl-library-tile" onClick={() => chooseHeaderFromLibrary(item)}>
                      <img src={item.url} alt={item.customLabel || item.filename || ""} loading="lazy" />
                    </button>
                  ))}
                  {!mediaLibraryLoading && mediaLibrary.length === 0 && <p className="adm-nl-empty">No images uploaded yet.</p>}
                </div>
              </div>
            )}
          </section>

          <section>
            <h3>YouTube video</h3>
            <p>Optional — email clients can't play video, so this shows as a clickable thumbnail.</p>
            {form.video ? (
              <div className="adm-nl-attached">
                <img src={form.video.thumbnail} alt="" />
                <div>
                  <span className="adm-nl-attached-type">video</span>
                  <strong>{form.video.title || form.video.url}</strong>
                </div>
                <button type="button" onClick={() => set("video", null)}>Remove</button>
              </div>
            ) : (
              <div className="adm-nl-fields">
                <input value={videoUrlInput} onChange={e => setVideoUrlInput(e.target.value)} placeholder="Paste a YouTube link…" />
                <input value={videoTitleInput} onChange={e => setVideoTitleInput(e.target.value)} placeholder="Caption (optional)" />
                <button type="button" className="adm-nl-attach-btn" onClick={attachVideo} disabled={!videoUrlInput.trim()}>+ Attach video</button>
              </div>
            )}
          </section>

          <section>
            <h3>Link buttons</h3>
            <p>Optional — a row of buttons, like "Watch on YouTube" / "Listen on Spotify".</p>
            {form.links.map((l, i) => (
              <div className="adm-nl-link-row" key={i}>
                <input value={l.label} onChange={e => updateLink(i, "label", e.target.value)} placeholder="Label" />
                <input value={l.url} onChange={e => updateLink(i, "url", e.target.value)} placeholder="https://…" />
                <button type="button" onClick={() => removeLink(i)} aria-label="Remove link">✕</button>
              </div>
            ))}
            <button type="button" className="adm-nl-attach-btn" onClick={addLink}>+ Add a link button</button>
          </section>

          <section>
            <h3>Attach a post or episode</h3>
            <p>Optional — pull in something already published, or skip this and just write.</p>
            {form.attachedContent ? (
              <div className="adm-nl-attached">
                {form.attachedContent.image && <img src={form.attachedContent.image} alt="" />}
                <div>
                  <span className="adm-nl-attached-type">{form.attachedContent.type}</span>
                  <strong>{form.attachedContent.title}</strong>
                </div>
                <button type="button" onClick={() => set("attachedContent", null)}>Remove</button>
              </div>
            ) : (
              <button type="button" className="adm-nl-attach-btn" onClick={openLibrary}>+ Choose from your posts &amp; episodes</button>
            )}
            {attachPickerOpen && (
              <div className="adm-nl-library">
                <div className="adm-nl-library-head">
                  <span>{libraryLoading ? "Loading…" : `${library.length} items`}</span>
                  <button type="button" onClick={() => setAttachPickerOpen(false)}>✕</button>
                </div>
                <div className="adm-nl-library-list">
                  {library.map((item, i) => (
                    <button type="button" key={i} className="adm-nl-library-item" onClick={() => pickAttachment(item)}>
                      {item.image && <img src={item.image} alt="" />}
                      <span>
                        <em>{item.type}</em>
                        <strong>{item.title}</strong>
                      </span>
                    </button>
                  ))}
                  {!libraryLoading && library.length === 0 && <p className="adm-nl-empty">Nothing published yet.</p>}
                </div>
              </div>
            )}
          </section>

          <section>
            <h3>Write the issue</h3>
            <div className="adm-nl-fields">
              <label><span>Small line above the title (optional)</span>
                <input value={form.kicker} onChange={e => set("kicker", e.target.value)} placeholder="e.g. A quick thought" />
              </label>
              <label><span>Title</span>
                <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="What's this issue about?" />
              </label>
              <label><span>Pull-quote or callout (optional)</span>
                <textarea rows="2" value={form.quote} onChange={e => set("quote", e.target.value)} placeholder="A line worth pulling out on its own" />
              </label>
              <label><span>Body</span>
                <textarea rows="10" value={form.body} onChange={e => set("body", e.target.value)} placeholder={"Write freely. Leave a blank line between paragraphs.\nWrap a word in **stars** to bold it."} />
              </label>
              <label><span>A question for readers (optional)</span>
                <input value={form.question} onChange={e => set("question", e.target.value)} placeholder="e.g. What's one thing you'd want future clients to know about you?" />
              </label>
              <label><span>Sign-off</span>
                <textarea rows="2" value={form.signoff} onChange={e => set("signoff", e.target.value)} />
              </label>
            </div>
          </section>

          <div className="adm-nl-actions">
            <button type="button" className="adm-nl-save-btn" onClick={saveDraft} disabled={saving}>
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button type="button" className="adm-nl-send-btn" onClick={sendNow} disabled={sending || isSent}>
              {isSent ? "Already sent" : sending ? "Sending…" : "Send now →"}
            </button>
          </div>
        </div>

        <div className="adm-nl-preview-wrap">
          <span>LIVE PREVIEW</span>
          <div className={`adm-nl-preview adm-nl-preview--${previewTheme}`}>
            {form.headerImage && <img className="adm-nl-p-header" src={form.headerImage} alt="" />}
            {form.kicker && <p className="adm-nl-p-kicker">{form.kicker}</p>}
            {form.title && <p className="adm-nl-p-title">{form.title}</p>}
            {form.video && (
              <div className="adm-nl-p-video">
                <div className="adm-nl-p-video-thumb">
                  <img src={form.video.thumbnail} alt="" />
                  <span className="adm-nl-p-play">▶</span>
                </div>
                {form.video.title && <p>▶ Watch: {form.video.title}</p>}
              </div>
            )}
            {form.quote && <div className="adm-nl-p-quote">{form.quote}</div>}
            {form.body && <div className="adm-nl-p-body" dangerouslySetInnerHTML={{ __html: toHtmlPreview(form.body) }} />}
            {form.attachedContent && (
              <div className="adm-nl-p-attached">
                {form.attachedContent.image && <img src={form.attachedContent.image} alt="" />}
                <strong>{form.attachedContent.title}</strong>
                {form.attachedContent.excerpt && <p>{form.attachedContent.excerpt}</p>}
                <span className="adm-nl-p-cta">{form.attachedContent.type === "podcast" ? "Listen to the episode →" : "Read the full post →"}</span>
              </div>
            )}
            {form.links.filter(l => l.label && l.url).length > 0 && (
              <div className="adm-nl-p-links">
                {form.links.filter(l => l.label && l.url).map((l, i) => <span key={i}>{l.label}</span>)}
              </div>
            )}
            {form.question && (
              <div className="adm-nl-p-question">
                <p>{form.question}</p>
                <span className="adm-nl-p-question-btn">Click here to share</span>
              </div>
            )}
            <p className="adm-nl-p-signoff">{form.signoff}</p>
          </div>
        </div>
      </div>

      <section className="adm-nl-history">
        <h3>Drafts &amp; sent issues {loadingIssues && <em>· loading…</em>}</h3>
        <div className="adm-nl-history-list">
          {issues.map(issue => (
            <div key={issue.id} className={`adm-nl-history-row${issue.id === issueId ? " active" : ""}`}>
              <button type="button" className="adm-nl-history-main" onClick={() => loadIssue(issue)}>
                <span className={`adm-nl-badge adm-nl-badge-${issue.status}`}>{issue.status}</span>
                <strong>{issue.title || issue.kicker || "Untitled draft"}</strong>
                {issue.status === "sent" && <em>{issue.recipientCount} sent</em>}
              </button>
              {issue.status !== "sent" && (
                <button type="button" className="adm-nl-history-del" onClick={() => deleteIssue(issue.id)} aria-label="Delete draft">🗑</button>
              )}
            </div>
          ))}
          {!loadingIssues && issues.length === 0 && <p className="adm-nl-empty">Nothing yet — your first draft will show up here.</p>}
        </div>
      </section>
    </div>
  );
}
